import { useQueries, useQuery } from '@tanstack/react-query';
import { IRealEstateUseType } from '../../Interfaces/IRealEstateUseType';
import { customFetcher } from '../useFetch';
import { ILandParcelEntry, IRealEstate } from '../../Interfaces/IRealEstate';
import { IRealEstateLandRegisterEntryQuery } from '../../Interfaces/IRealEstateLandRegisterEntryQuery';

//////////////////// BORIS/ALKIS (Brandenburg) — Adresse → Kataster + Bodenrichtwert
export interface IBorisResult {
  adresse?: string;
  fehler?: string;
  koordinaten?: { lon: number; lat: number; quelle?: string | null };
  flurstueck?: {
    gemarkung?: string | null;
    flur?: string | null;
    flurstuecksnummer?: string | null;
    flurstueckskennzeichen?: string | null;
    flaeche_m2?: string | null;
    gemeinde?: string | null;
    landkreis?: string | null;
    lage?: string | null;
    fehler?: string;
  };
  bodenrichtwert?: {
    bodenrichtwert_eur_m2?: number | null;
    bodenrichtwert_anzeige?: string | null;
    stichtag?: string | null;
    nutzungsart?: string | null;
    entwicklungszustand?: string | null;
    hinweis?: string;
    fehler?: string;
  };
}

/**
 * Ruft die BORIS/ALKIS-Abfrage (Brandenburg) zu einer Adresse auf. Liefert
 * Gemarkung/Flur/Flurstück + Bodenrichtwert zur Vorbefüllung. Liefert immer ein
 * Objekt (Fehler unter `fehler`). Hinweis: Backend-Route `/realestate/boris`
 * muss registriert sein (Recompile nach Hinzufügen der CustomPath).
 */
export const fetchBorisData = (adresse: string): Promise<IBorisResult> =>
  customFetcher({
    endpoint: `/realestate/boris?adresse=${encodeURIComponent(adresse)}`,
  })
    .then(r => (r && typeof r === 'object' ? (r as IBorisResult) : {}))
    .catch(() => ({ fehler: 'BORIS-Abfrage fehlgeschlagen.' }) as IBorisResult);

//////////////////// Verwandte Grundstücke (Phase 4b — Karten-Hervorhebung)
export interface IRelatedRealEstate {
  idRealEstate: number;
  Latitude?: number | null;
  Longitude?: number | null;
  LandRegister?: string | null;
  SheetLandRegister?: string | null;
  Parcel?: string | null;
  LandParcel?: string | null;
  Street?: string | null;
  StreetNr?: string | null;
  ZipCode?: string | null;
  Place?: string | null;
}

/**
 * Grundstücke, die mit dem gegebenen „verwandt" sind (gleicher Eigentümer oder
 * gleiches Blatt+Gemarkung) und Koordinaten haben — für die rote Hervorhebung
 * auf der Karte. Erst ab persistiertem Grundstück (idRealEstate > 0) aktiv.
 */
export const useRelatedRealEstates = (idRealEstate: number) => {
  const { data } = useQuery<IRelatedRealEstate[]>({
    enabled: idRealEstate > 0,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    queryKey: ['realestate', 'related', idRealEstate],
    queryFn: () =>
      customFetcher({ endpoint: `/realestate/${idRealEstate}/related` })
        .then(r => (Array.isArray(r) ? (r as IRelatedRealEstate[]) : []))
        .catch(() => [] as IRelatedRealEstate[]),
  });
  return { relatedRealEstates: Array.isArray(data) ? data : [] };
};

//////////////////// RealEstateUseTypes
export const useGeRealEstateUseTypes = () => {
  const { data: realEstateUseTypes, isLoading: isLoadingRealEstateUseTypes } =
    useQuery<IRealEstateUseType[]>({
      refetchOnWindowFocus: false,
      queryKey: ['realestateusetype'],
      queryFn: () =>
        customFetcher({
          endpoint: '/realestateusetype',
        }),
    });

  return { realEstateUseTypes, isLoadingRealEstateUseTypes };
};

export const useGetRealEstate = () => {
  const { data: realEstates, isLoading: isLoadingRealEstate } = useQuery<
    IRealEstate[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['realestate'],
    queryFn: () =>
      customFetcher({
        endpoint: '/realestate',
      }),
  });

  return { realEstates, isLoadingRealEstate };
};

export interface IRealEstateSearchHit {
  idRealEstate: number;
  LandRegister?: string | null;
  SheetLandRegister?: string | null;
  LandParcel?: string | null;
  Parcel?: string | null;
  Area?: number | null;
  Street?: string | null;
  StreetNr?: string | null;
  ZipCode?: string | null;
  Place?: string | null;
  /** Eigentümer (kommagetrennt) aus der Asset-Brücke (Pfad B), für Dubletten-Hinweis. */
  Owners?: string | null;
}

/**
 * Freitext-Suche in bestehenden RealEstates — wird im Vorgangs-Edit beim
 * Anlegen eines Grundstuecks aufgerufen, um Duplikate zu vermeiden. Sucht
 * in Gemarkung, Blattnummer, Flur/Flurstueck, Adresse und Eigentuemer. Erst
 * ab 2 Zeichen aktiv, debounce ist Caller-Sache (Hook macht keinen).
 */
export const useSearchRealEstates = (q: string | null | undefined) => {
  const term = (q ?? '').trim();
  const enabled = term.length >= 2;
  const { data, isFetching } = useQuery<IRealEstateSearchHit[]>({
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    queryKey: ['realestate', 'search', term.toLowerCase()],
    queryFn: () =>
      customFetcher({
        endpoint: `/realestate/search?q=${encodeURIComponent(term)}`,
      })
        // Backend kann temporaer einen Error-Body (Objekt) statt Array liefern
        // — z. B. wenn der Endpoint noch nicht registriert ist oder swagger
        // einen 4xx-JSON-Body zurueckschickt. Beides defensiv abfangen, damit
        // MUI-Autocomplete nicht mit ``options.filter is not a function``
        // crasht.
        .then(r => (Array.isArray(r) ? r : []))
        .catch(() => [] as IRealEstateSearchHit[]),
  });
  return {
    realEstateHits: Array.isArray(data) ? data : [],
    isSearchingRealEstate: isFetching,
  };
};

export const useGetRealEstateLandRegisterEntries = () => {
  const {
    data: realEstateLandRegisterEntries,
    isLoading: isLoadingRealEstateLandRegisterEntries,
  } = useQuery<ILandParcelEntry[]>({
    refetchOnWindowFocus: false,
    queryKey: ['realestatelandregisterentry'],
    queryFn: () =>
      customFetcher({
        endpoint: '/realestatelandregisterentry',
      }),
  });

  return {
    realEstateLandRegisterEntries,
    isLoadingRealEstateLandRegisterEntries,
  };
};

export const useGetRealEstateLandRegisterEntryQueryByRealEstate = (
  idRealEstateLand?: number | number[],
  enabled: boolean = true
) => {
  const ids = Array.isArray(idRealEstateLand)
    ? Array.from(new Set(idRealEstateLand)).filter(
        (id): id is number => typeof id === 'number' && Number.isFinite(id)
      )
    : typeof idRealEstateLand === 'number' && Number.isFinite(idRealEstateLand)
      ? [idRealEstateLand]
      : [];

  const queries = useQueries({
    queries: ids.map(id => ({
      refetchOnWindowFocus: false,
      queryKey: ['realestatelandregisterentryquery', 'byRealeState', id],
      queryFn: () =>
        customFetcher({
          endpoint: `/realestatelandregisterentryquery/byRealeState/${id}`,
        }) as Promise<IRealEstateLandRegisterEntryQuery[]>,
      enabled: enabled && ids.length > 0,
      staleTime: 60_000,
    })),
  });

  const realEstateLandRegisterEntryQueries: IRealEstateLandRegisterEntryQuery[] =
    queries.flatMap(q => q.data ?? []);

  const isLoadingRealEstateLandRegisterEntryQueries =
    enabled && queries.some(q => q.isLoading);

  const refetchRealEstateLandRegisterEntryQueries = async () => {
    const results = await Promise.all(
      queries.map(q => q.refetch?.()).filter(Boolean)
    );
    return results;
  };

  return {
    realEstateLandRegisterEntryQueries,
    isLoadingRealEstateLandRegisterEntryQueries,
    refetchRealEstateLandRegisterEntryQueries,
  };
};
